import React, { forwardRef, useContext, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import ReactQuill from 'react-quill';
import CustomQuillEditor, { CustomQuillEditorHandle } from '../fields/CustomQuillEditor';
import FormFieldContext from '../FormFieldContext';
import 'react-quill/dist/quill.snow.css';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import '../../../style/scss/quill-customized.scss';;

interface TextareaInputProps {
    id?: string;
    name: string;
    value: string;
    placeholder?: string;
    identifier?: string;
    autoFocus?: boolean;
    rows?: number;
    isNote?: boolean;
    disabled?: boolean;
    enableStyling?: 'simple' | 'extended' | 'comprehensive';
    showToolbarInitially?: boolean;
    viewInEditMode?: boolean;
    isInvalid?: boolean;
    onChange: (value: string | React.ChangeEvent<HTMLTextAreaElement>) => void;
    onTabClose?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
}

export interface TextareaInputHandle {
    insertAtCursor: (text: string) => void;
    focus: () => void;
}

const TextareaInput = forwardRef<TextareaInputHandle, TextareaInputProps>(({
    id, name, value, placeholder, autoFocus, rows = 2, isNote, disabled, enableStyling, 
    showToolbarInitially, viewInEditMode, identifier, isInvalid, onChange, onTabClose, onFocus, onBlur
}, ref) => {
    const { t } = useTranslation();
    const { errorMessages } = useGlobalContext();
    const { editing } = useContext(FormFieldContext);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const quillRef = useRef<CustomQuillEditorHandle>(null);
    // const editorRef = useRef<HTMLDivElement | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const textareaLineHeight = 1.2; // 1.05
    const textareaPadding = 0.4;  // 3.5
    const baseFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);

    useImperativeHandle(ref, () => ({
        insertAtCursor: (text: string) => {
            if (enableStyling && quillRef.current) {
                quillRef.current.insertAtCursor(text);
            } else {
                insertIntoTextareaAtCursor(text);
            }
        },
        focus: () => {
            if (enableStyling && quillRef.current) {
                quillRef.current.focus();
            } else if (textareaRef.current) {
                textareaRef.current.focus();
            }
        }
    }));
    
    // Calculates the textarea height based on the given rows
    const setTextAreaHeight = (elem: HTMLElement) => {
        // Calculate the default min height of 2 rows (or more if given) in rem
        const minHeight = (rows * textareaLineHeight) + (2 * textareaPadding);

        // Calculate the default height in pixels
        const minHeightPx = minHeight * baseFontSize;

        // Set the default height to the calculated min height
        elem.style.minHeight = `${minHeight}rem`;

        if (elem.scrollHeight > minHeightPx) {
            elem.style.height = `${elem.scrollHeight / baseFontSize}rem`;
        }
    };

    // Sets and grows the height of the field
    useLayoutEffect(() => {
        // For the normal textarea
        if (textareaRef.current) {
            setTextAreaHeight(textareaRef.current);
        }

        // For the Quill styled textarea editor
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const editorElem = editor.root as HTMLElement;
            setTextAreaHeight(editorElem);
        }
        // if (editorRef.current) {
        //     // const editor = edit.current.getEditor();
        //     // const editorElem = editor.root as HTMLElement;
        //     const editorElem = editorRef.current as HTMLElement
        //     setTextAreaHeight(editorElem);
        // }
    }, [value, rows]);

    // Makes the text area focused after one click when its in view in edit mode 
    useEffect(() => {
        if (editing && viewInEditMode) {
            if (enableStyling && quillRef.current) {
                const editor = quillRef.current.getEditor();
                editor.focus();
            // if (enableStyling && editorRef.current) {
            //     const editorElem = editorRef.current as HTMLElement
            //     editorElem.focus();
            } else if (textareaRef.current) {
                textareaRef.current.focus();
            }
        }
    }, [editing, viewInEditMode, enableStyling]);

    // Insert a string into the text area at the cursor
    const insertIntoTextareaAtCursor = (text: string) => {
        const textarea = textareaRef.current;

        if (textarea) {
            const start = textarea.selectionStart ?? value.length;
            const end = textarea.selectionEnd ?? value.length;
            const updatedValue = `${value.slice(0, start)}${text}${value.slice(end)}`;

            onChange(updatedValue);

            requestAnimationFrame(() => {
                textarea.focus();
                textarea.setSelectionRange(start + text.length, start + text.length);
            });
        }
    };

    // Get the error message from the errorState
    const errorMessage = errorMessages[name];

    return (
        <div className={`textarea-input ${isNote ? 'note' : ''}`}>
            {enableStyling ? (
                <CustomQuillEditor 
                    ref={quillRef}
                    value={value}
                    enableStyling={enableStyling}
                    showToolbarInitially={showToolbarInitially}
                    onChange={value => onChange(value)}
                    placeholder={placeholder}
                    className={`${(errorMessage || isInvalid) ? 'is-invalid' : ''} ${isNote ? 'note-form' : ''} ${isFocused ? 'focused' : ''}`}
                    identifier={identifier}
                    onTabClose={onTabClose}
                    onFocus={() => {
                        setIsFocused(true);
                        onFocus?.();
                    }}
                    onBlur={() => {setIsFocused(false); onBlur?.();}}
                />

                // <HtmlTextareaInput 
                //     ref={editorRef}
                //     value={value}
                //     enableStyling={enableStyling}
                //     showToolbarInitially={showToolbarInitially}
                //     onChange={value => onChange(value)}
                //     placeholder={placeholder}
                //     className={`${errorMessage ? 'is-invalid' : ''} ${isNote ? 'note-form' : ''} ${isFocused ? 'focused' : ''}`}
                //     identifier={identifier}
                //     onTabClose={onTabClose}
                //     onFocus={() => setIsFocused(true)}
                //     onBlur={() => setIsFocused(false)}
                // />
            ) : (
                <textarea
                    ref={textareaRef}
                    id={id}
                    name={name}
                    placeholder={t(placeholder)}
                    disabled={disabled}
                    value={value}
                    onChange={e => onChange(e)}
                    className={`${(errorMessage || isInvalid) ? 'is-invalid' : ''} ${isNote ? 'note-form' : ''}`}
                    autoFocus={autoFocus}
                    onFocus={() => onFocus?.()}
                    onBlur={() => onBlur?.()}
                />
            )}
        </div>
    )
});

export default TextareaInput;